<!--
  Copyright (C) 2022 - Jeci SARL - https://jeci.fr

  This program is free software: you can redistribute it and/or modify
  it under the terms of the GNU Affero General Public License as
  published by the Free Software Foundation, either version 3 of the
  License, or (at your option) any later version.

  This program is distributed in the hope that it will be useful,
  but WITHOUT ANY WARRANTY; without even the implied warranty of
  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
  GNU Affero General Public License for more details.

  You should have received a copy of the GNU Affero General Public License
  along with this program.  If not, see https://www.gnu.org/licenses/agpl-3.0.html.
-->

<template>
  <div v-if="computedPath.length > 0" class="complex-breadcrumb">
    <!-- Mode Linéaire -->
    <div v-if="displayMode === 'linear'" class="breadcrumb-linear">
      <template v-for="(item, index) in computedPath" :key="index">
        <!-- Séparateur -->
        <i
          v-if="index > 0"
          class="ri-arrow-right-s-line breadcrumb-separator"
        ></i>

        <!-- Élément -->
        <div class="breadcrumb-item">
          <router-link
            v-if="isItemClickable(item, index)"
            :to="item.to"
            class="breadcrumb-link"
          >
            <i :class="getItemIcon(item)" class="breadcrumb-icon"></i>
            <span
              v-if="item.name"
              v-tooltip.bottom="item.name"
              class="breadcrumb-text"
            >
              {{ item.name }}
            </span>
          </router-link>
          <span v-else class="breadcrumb-current">
            <i :class="getItemIcon(item)" class="breadcrumb-icon"></i>
            <span
              v-if="item.name"
              v-tooltip.bottom="item.name"
              class="breadcrumb-text"
            >
              {{ item.name }}
            </span>
          </span>

          <!-- Menu d'actions sur le dernier élément -->
          <PrimeButton
            v-if="isLastItem(index) && showActions && !isInSearch"
            v-tooltip.bottom="$t('moreActions')"
            type="button"
            class="breadcrumb-action-btn"
            icon="ri-more-2-fill"
            rounded
            text
            severity="secondary"
            @click="toggleActionMenu($event, item)"
          />
        </div>
      </template>
    </div>

    <!-- Mode Ellipsis -->
    <div v-else class="breadcrumb-ellipsis-mode">
      <!-- Home -->
      <template
        v-if="
          showHome &&
          computedPath.length > 0 &&
          computedPath[0].nodeType === 'home'
        "
      >
        <div class="breadcrumb-item">
          <router-link :to="computedPath[0].to" class="breadcrumb-link">
            <i class="ri-home-4-line breadcrumb-icon"></i>
          </router-link>
        </div>
        <i class="ri-arrow-right-s-line breadcrumb-separator"></i>
      </template>

      <!-- Espace (masqué sur très petit écran) -->
      <template v-if="!isVerySmallScreen && workspaceItem">
        <div class="breadcrumb-item">
          <router-link :to="workspaceItem.to" class="breadcrumb-link">
            <i class="ri-building-line breadcrumb-icon"></i>
            <span v-tooltip.bottom="workspaceItem.name" class="breadcrumb-text">
              {{ workspaceItem.name }}
            </span>
          </router-link>
        </div>
        <i class="ri-arrow-right-s-line breadcrumb-separator"></i>
      </template>

      <!-- Bouton Ellipsis avec menu (masqué si menu vide) -->
      <template v-if="ellipsisMenuItems.length > 0">
        <PrimeButton
          type="button"
          class="breadcrumb-ellipsis-btn"
          icon="ri-more-fill"
          rounded
          text
          severity="secondary"
          @click="toggleEllipsisMenu($event)"
        />
        <PrimeMenu ref="ellipsisMenu" :model="ellipsisMenuItems" popup>
          <template #item="{ item }">
            <div class="ellipsis-menu-item">
              <router-link :to="item.route" class="ellipsis-menu-link">
                <i :class="item.icon" class="ellipsis-menu-icon"></i>
                <span class="ellipsis-menu-label">{{ item.label }}</span>
              </router-link>
            </div>
          </template>
        </PrimeMenu>
      </template>

      <!-- Avant-dernier élément (masqué sur petit écran) -->
      <template v-if="!isSmallScreen && secondToLastItem">
        <i class="ri-arrow-right-s-line breadcrumb-separator"></i>
        <div class="breadcrumb-item">
          <router-link :to="secondToLastItem.to" class="breadcrumb-link">
            <i
              :class="getItemIcon(secondToLastItem)"
              class="breadcrumb-icon"
            ></i>
            <span
              v-tooltip.bottom="secondToLastItem.name"
              class="breadcrumb-text"
            >
              {{ secondToLastItem.name }}
            </span>
          </router-link>
        </div>
      </template>

      <!-- Dernier élément -->
      <i class="ri-arrow-right-s-line breadcrumb-separator"></i>
      <div class="breadcrumb-item">
        <router-link
          v-if="isInConsultationMode || isInSearch"
          :to="lastItem.to"
          class="breadcrumb-link"
        >
          <i :class="getItemIcon(lastItem)" class="breadcrumb-icon"></i>
          <span v-tooltip.bottom="lastItem.name" class="breadcrumb-text">
            {{ lastItem.name }}
          </span>
        </router-link>
        <span v-else class="breadcrumb-current">
          <i :class="getItemIcon(lastItem)" class="breadcrumb-icon"></i>
          <span v-tooltip.bottom="lastItem.name" class="breadcrumb-text">
            {{ lastItem.name }}
          </span>
        </span>

        <!-- Menu d'actions sur le dernier élément -->
        <PrimeButton
          v-if="showActions && !isInSearch"
          v-tooltip.bottom="$t('moreActions')"
          type="button"
          class="breadcrumb-action-btn"
          icon="ri-more-2-fill"
          rounded
          text
          severity="secondary"
          @click="toggleActionMenu($event, lastItem)"
        />
      </div>
    </div>

    <!-- Menu d'actions -->
    <ActionMenu
      v-if="showActions"
      ref="actionMenu"
      :actionMenuItems="actionMenuItems"
    />
  </div>
</template>

<script>
import { computed, ref, inject } from "vue";
import { useRoute } from "vue-router";
import { storeToRefs } from "pinia";
import alfrescoFileService from "@/services/AlfrescoFileService.js";
import alfrescoNodeService from "@/services/AlfrescoNodeService.js";
import { useScreenStore } from "@/stores/screen.js";
import ActionMenu from "../menu/ActionMenu.vue";
import Button from "primevue/button";
import Menu from "primevue/menu";
import Tooltip from "primevue/tooltip";

export default {
  name: "ComplexBreadcrumb",
  components: {
    ActionMenu,
    PrimeButton: Button,
    PrimeMenu: Menu,
  },
  directives: {
    tooltip: Tooltip,
  },
  props: {
    /**
     * Chemin pré-calculé à afficher
     * Array de { name, to, nodeType, isFolder, id }
     */
    path: {
      type: Array,
      default: () => [],
    },
    /**
     * Noeud actuel (utilisé pour construire le path si path n'est pas fourni)
     */
    currentNode: {
      type: Object,
      default: null,
    },
    /**
     * Mode d'affichage : 'auto' | 'linear' | 'ellipsis'
     * 'auto' : sélectionne automatiquement selon la longueur du chemin
     */
    mode: {
      type: String,
      default: "auto",
      validator: (value) => ["auto", "linear", "ellipsis"].includes(value),
    },
    /**
     * Seuil pour basculer en mode ellipsis (en mode auto)
     * Par défaut : 4 éléments (5 en consultation)
     */
    ellipsisThreshold: {
      type: Number,
      default: null,
    },
    /**
     * Afficher le menu d'actions sur le dernier élément
     */
    showActions: {
      type: Boolean,
      default: true,
    },
    /**
     * Mode recherche : le dernier élément reste cliquable
     */
    isInSearch: {
      type: Boolean,
      default: false,
    },
    /**
     * Mode consultation : le dernier élément reste cliquable
     */
    isInConsultation: {
      type: Boolean,
      default: false,
    },
    /**
     * Afficher l'élément home (premier élément du breadcrumb)
     */
    showHome: {
      type: Boolean,
      default: true,
    },
    /**
     * Route pour le home (premier élément)
     */
    homeRoute: {
      type: [String, Object],
      default: () => ({ name: "home" }),
    },
    /**
     * Nom de la route pour les espaces/workspaces
     */
    workspaceRouteName: {
      type: String,
      default: "espace",
    },
    /**
     * Nom de la route pour les dossiers
     */
    folderRouteName: {
      type: String,
      default: "espace-folder",
    },
  },
  emits: ["reload", "action"],
  setup(props) {
    const route = useRoute();
    const ellipsisMenu = ref(null);
    const actionMenu = ref(null);
    const actionMenuItems = ref([]);

    // Inject getBreadcrumbActions from parent
    const getBreadcrumbActions = inject("getBreadcrumbActions", () => []);

    // Screen size management
    const screenStore = useScreenStore();
    const { screenWidth } = storeToRefs(screenStore);

    // Auto-detect consultation mode from route name
    const isInConsultationMode = computed(() => {
      return props.isInConsultation || route.name?.startsWith("consultation");
    });

    const isSmallScreen = computed(() => screenWidth.value < 768);
    const isVerySmallScreen = computed(() => screenWidth.value < 480);

    // Compute the path to display
    const computedPath = computed(() => {
      let path;
      if (props.path && props.path.length > 0) {
        path = props.path;
      } else if (!props.currentNode || !props.currentNode.path) {
        return [];
      } else {
        path = buildPathFromNode(props.currentNode);
      }

      if (!props.showHome && path.length > 0 && path[0].nodeType === "home") {
        return path.slice(1);
      }
      return path;
    });

    // Build path from a node
    const buildPathFromNode = (node) => {
      const elements = node.path?.elements || [];
      const filteredElements = elements
        .slice(2)
        .filter((item) => alfrescoFileService.isPathElementVisible(item, 1));
      let workspaceId = null;

      const path = filteredElements.map((item) => {
        const pathItem = {
          id: item.id,
          name: item.name,
          nodeType: item.nodeType,
          isFolder:
            item.nodeType === "cm:folder" || item.nodeType === "st:site",
        };

        if (item.nodeType === "st:site") {
          workspaceId = item.name;
          pathItem.to = {
            name: props.workspaceRouteName,
            params: { id: item.name },
          };
        } else if (workspaceId) {
          pathItem.to = {
            name: props.folderRouteName,
            params: { id: workspaceId, folderId: item.id },
          };
        } else {
          pathItem.to = {};
        }

        return pathItem;
      });

      // Add current node if it is a folder
      if (
        node.isFolder &&
        node.name !== "documentLibrary" &&
        !path.some((p) => p.id === node.id)
      ) {
        path.push({
          id: node.id,
          name: node.name,
          nodeType: node.nodeType,
          isFolder: true,
          to: workspaceId
            ? {
                name: props.folderRouteName,
                params: { id: workspaceId, folderId: node.id },
              }
            : {},
        });
      }

      // Add home at the beginning
      path.unshift({
        nodeType: "home",
        to:
          typeof props.homeRoute === "string"
            ? { name: props.homeRoute }
            : props.homeRoute,
      });

      return path;
    };

    // Computed display mode
    const displayMode = computed(() => {
      if (props.mode !== "auto") {
        return props.mode;
      }

      // On small screens, always use ellipsis if more than 2 elements
      if (isSmallScreen.value && computedPath.value.length > 2) {
        return "ellipsis";
      }

      const threshold =
        props.ellipsisThreshold ?? (isInConsultationMode.value ? 5 : 4);
      return computedPath.value.length > threshold ? "ellipsis" : "linear";
    });

    // Index of the first element after home and workspace (hardcoded items in ellipsis mode)
    const ellipsisStartOffset = computed(() => {
      let offset = 0;
      if (
        computedPath.value.length > 0 &&
        computedPath.value[0].nodeType === "home"
      ) {
        offset++;
      }
      if (
        computedPath.value.length > offset &&
        computedPath.value[offset].nodeType === "st:site"
      ) {
        offset++;
      }
      return offset;
    });

    // Workspace item (first st:site in path)
    const workspaceItem = computed(() => {
      return (
        computedPath.value.find((item) => item.nodeType === "st:site") || null
      );
    });

    // Last item
    const lastItem = computed(() => {
      return computedPath.value[computedPath.value.length - 1] || {};
    });

    // Second to last item
    const secondToLastItem = computed(() => {
      return computedPath.value.length > ellipsisStartOffset.value + 1
        ? computedPath.value[computedPath.value.length - 2]
        : null;
    });

    // Items for the ellipsis menu
    const ellipsisMenuItems = computed(() => {
      const start = ellipsisStartOffset.value;
      let startIndex, endIndex;

      if (isVerySmallScreen.value) {
        // Very small screen: all except home and last (includes site)
        startIndex =
          computedPath.value.length > 0 &&
          computedPath.value[0].nodeType === "home"
            ? 1
            : 0;
        endIndex = computedPath.value.length - 1;
      } else if (isSmallScreen.value) {
        // Small screen: between site and last (site displayed directly)
        startIndex = start;
        endIndex = computedPath.value.length - 1;
      } else {
        // Large screen: between site and second to last
        startIndex = start;
        endIndex = computedPath.value.length - 2;
      }

      // Nothing to display in the menu
      if (startIndex >= endIndex) {
        return [];
      }

      return computedPath.value.slice(startIndex, endIndex).map((item) => ({
        label: item.name,
        icon: getItemIconClass(item),
        route: item.to,
      }));
    });

    // Get icon class for an item
    const getItemIconClass = (item) => {
      if (item.nodeType === "home") {
        return "ri-home-4-line";
      }
      if (item.nodeType === "st:site") {
        return "ri-building-line";
      }
      if (item.isFolder) {
        return "ri-folder-2-line";
      }
      return "ri-file-text-line";
    };

    // Check if an item is clickable
    const isItemClickable = (item, index) => {
      const isLast = index === computedPath.value.length - 1;
      if (!isLast) {
        return true;
      }
      return props.isInSearch || isInConsultationMode.value;
    };

    // Check if it is the last item
    const isLastItem = (index) => {
      return index === computedPath.value.length - 1;
    };

    // Toggle ellipsis menu
    const toggleEllipsisMenu = (event) => {
      ellipsisMenu.value?.toggle(event);
    };

    // Toggle action menu
    const toggleActionMenu = (event, item) => {
      const target = event.currentTarget || event.target;

      if (!item.id) {
        actionMenuItems.value = getBreadcrumbActions(item);
        actionMenu.value?.getActionMenuRef()?.toggle({ currentTarget: target });
        return;
      }

      alfrescoNodeService.getNode(item.id).then((node) => {
        actionMenuItems.value = getBreadcrumbActions(node);
        actionMenu.value?.getActionMenuRef()?.toggle({ currentTarget: target });
      });
    };

    return {
      ellipsisMenu,
      actionMenu,
      actionMenuItems,
      computedPath,
      displayMode,
      workspaceItem,
      lastItem,
      secondToLastItem,
      ellipsisMenuItems,
      getItemIcon: getItemIconClass,
      isItemClickable,
      isLastItem,
      isInConsultationMode,
      toggleEllipsisMenu,
      toggleActionMenu,
      isSmallScreen,
      isVerySmallScreen,
    };
  },
};
</script>

<style scoped>
.complex-breadcrumb {
  display: flex;
  align-items: center;
}

/* Mode linéaire */
.breadcrumb-linear {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0;
}

/* Mode ellipsis */
.breadcrumb-ellipsis-mode {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0;
}

/* Élément du breadcrumb */
.breadcrumb-item {
  display: flex;
  align-items: center;
}

/* Séparateur */
.breadcrumb-separator {
  color: var(--p-text-muted-color);
  font-size: 1.25rem;
  margin: 0 0.25rem;
}

/* Lien cliquable */
.breadcrumb-link {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  text-decoration: none;
  color: var(--p-text-muted-color);
  transition: color 0.2s;
}

.breadcrumb-link:hover {
  color: var(--p-primary-color);
}

/* Élément courant (non cliquable) */
.breadcrumb-current {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  color: var(--p-text-color);
  font-weight: 600;
  cursor: default;
}

/* Icône */
.breadcrumb-icon {
  font-size: 1.125rem;
  flex-shrink: 0;
}

/* Texte */
.breadcrumb-text {
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  max-width: 10rem;
}

/* Bouton ellipsis */
.breadcrumb-ellipsis-btn {
  height: 1.5rem !important;
  width: 1.5rem !important;
  margin: 0 0.25rem;
}

.breadcrumb-ellipsis-btn:hover {
  background-color: var(--p-primary-color) !important;
  color: var(--p-primary-contrast-color) !important;
}

/* Bouton d'action */
.breadcrumb-action-btn {
  height: 1.5rem !important;
  width: 1.5rem !important;
  margin-left: 0.25rem;
}

/* Menu ellipsis */
.ellipsis-menu-item {
  padding: 0.5rem 0.75rem;
}

.ellipsis-menu-link {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  text-decoration: none;
  color: var(--p-text-color);
}

.ellipsis-menu-link:hover {
  color: var(--p-primary-color);
}

.ellipsis-menu-icon {
  font-size: 1rem;
}

.ellipsis-menu-label {
  font-size: 0.875rem;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .breadcrumb-text {
    max-width: 8rem;
  }
}

@media (max-width: 480px) {
  .breadcrumb-text {
    max-width: 6rem;
  }

  .breadcrumb-separator {
    margin: 0 0.125rem;
  }
}
</style>
